import { useState } from 'react'
import { Download, RotateCcw, TrendingDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useObjectUrl } from '@/hooks/use-object-url'
import { useI18n } from '@/lib/i18n'
import { formatBytes } from '@/lib/format'
import { downloadBlob } from '@/lib/download'
import type { CompressionResult } from './lib'

interface ImageResultCardProps {
  originalName: string
  originalSize: number
  result: CompressionResult
  onReset: () => void
}

/** Before/after comparison with preview, savings ratio and download. */
export function ImageResultCard({ originalName, originalSize, result, onReset }: ImageResultCardProps) {
  const previewUrl = useObjectUrl(result.blob)
  const [dimensions, setDimensions] = useState<string | null>(null)
  const { t } = useI18n()

  const savingsRatio = 1 - result.blob.size / originalSize
  const savingsPercent = Math.round(savingsRatio * 100)

  return (
    <div className="rounded-xl border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <TrendingDown className="h-5 w-5 text-primary" aria-hidden />
        <p className="font-medium">{t('imgResultComplete')}</p>
      </div>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row">
        {previewUrl && (
          <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted sm:w-48">
            <img
              src={previewUrl}
              alt={t('imgPreviewAlt')}
              className="max-h-48 w-auto object-contain"
              onLoad={(event) => {
                const img = event.currentTarget
                setDimensions(`${img.naturalWidth}×${img.naturalHeight}`)
              }}
            />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{dimensions ?? t('imgLoadingPreview')}</p>

          <dl className="mt-3 grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-muted p-3">
              <dt className="text-xs text-muted-foreground">{t('imgOriginal')}</dt>
              <dd className="mt-1 font-mono text-sm font-medium">{formatBytes(originalSize)}</dd>
            </div>
            <div className="rounded-lg bg-muted p-3">
              <dt className="text-xs text-muted-foreground">{t('imgCompressed')}</dt>
              <dd className="mt-1 font-mono text-sm font-medium">{formatBytes(result.blob.size)}</dd>
            </div>
            <div className="rounded-lg bg-primary/10 p-3">
              <dt className="text-xs text-primary">{t('imgSaved')}</dt>
              <dd
                className={
                  savingsPercent > 0
                    ? 'mt-1 font-mono text-sm font-bold text-primary'
                    : 'mt-1 font-mono text-sm font-bold text-destructive'
                }
              >
                {savingsPercent > 0 ? `${savingsPercent}%` : t('imgLarger', { pct: -savingsPercent })}
              </dd>
            </div>
          </dl>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              className="flex-1"
              onClick={() => downloadBlob(result.blob, originalName)}
            >
              <Download className="mr-1" aria-hidden />
              {t('commonDownload')}
            </Button>
            <Button variant="outline" className="flex-1" onClick={onReset}>
              <RotateCcw className="mr-1" aria-hidden />
              {t('imgNewImage')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}